/* eslint-disable react-hooks/rules-of-hooks */
"use client"

import { ColumnDef, RowData } from "@tanstack/react-table"
import { Check, MoreHorizontal } from "lucide-react"
import { Checkbox } from "@/Components/ui/checkbox"
import { Button } from "@/Components/ui/button"
import { format } from "date-fns"
import { TaskForTable } from "../Plan"
import { dateformat } from "@/utils/GlobalConstants"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/Components/ui/dropdown-menu"
import { useContext } from "react"
import { AppContext } from "@/App"
import { DeliverableIcon, CompletedIcon, StatusReportIcon, CharterIcon, MilestoneIcon, DependenciesIcon } from "../../ui/Icons/TooltipIcon";
import { PageLockContext } from "@/Components/PageLockProvider"

declare module '@tanstack/react-table' {
    //allows us to define custom properties for our columns
    interface ColumnMeta<TData extends RowData, TValue> {
        filterVariant?: 'text' | 'date-range' | 'select' | 'bool-select'
    }
}

export function getTaskColumns(HandleDeleteClicked: (taskIds: number[]) => void,
    HandleEditClicked: (taskId: number) => void,
    HandleCompleteClicked: (taskIds: number[]) => void): ColumnDef<TaskForTable>[] {
    const app = useContext(AppContext);
    const lock = useContext(PageLockContext);

    const columns: ColumnDef<TaskForTable>[] = [];

    if (app.authLevel > 3 && !lock.viewOnly) {
        columns.push(
            {
                id: "select",
                header: ({ table }) => (
                    <Checkbox
                        checked={
                            table.getIsAllPageRowsSelected() ||
                            (table.getIsSomePageRowsSelected() && "indeterminate")
                        }
                        onClick={(e) => e.stopPropagation()}
                        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                        aria-label="Select all"
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        checked={row.getIsSelected()}
                        onClick={(e) => e.stopPropagation()}
                        onCheckedChange={(value) => row.toggleSelected(!!value)}
                        aria-label="Select row"
                    />
                ),
                enableSorting: false,
                enableHiding: false,
            });
    }

    columns.push(
        {
            id: "id",
            accessorKey: "id",
            header: "ID",
            cell: ({ row }) => {
                const taskId = row.getValue("id");
                return <div className="text-center">{taskId as number}</div>
            },
            filterFn: (row, columnId, filterValue) => {
                // needs to match exact number
                const value = row.getValue(columnId) as number;
                const filterNumber = typeof filterValue === "string" ? parseInt(filterValue, 10) : filterValue;
                return value === filterNumber;
            },
            size: 50,
        },
        {
            id: "parentTaskId",
            accessorKey: "parentTaskId",
            header: "Parent Task",
            cell: ({ row }) => {
                const parentTaskId = row.getValue("parentTaskId");
                return <div className="text-center">{parentTaskId as number}</div>
            },
            size: 80,
        }
    );

    if (app.selectedProject?.projectTypeId === 2) {
        columns.push(
            {
                id: "workstream",
                accessorKey: "workstream",
                header: "WS",
                meta: {
                    filterVariant: 'select',
                },
                cell: ({ row }) => {
                    const workstream = row.getValue("workstream");
                    return <div className="text-center">{workstream as string}</div>
                },
                size: 120,
            });
        }
        
    columns.push(
        {
            id: "workPackage",
                accessorKey: "workPackage",
                    header: "WP",
                        meta: {
                filterVariant: 'select',
            },
            cell: ({ row }) => {
                const workPackage = row.getValue("workPackage");
                return <div className="text-center">{workPackage as string}</div>
            },
                size: 120,
        },
        {
            id: "name",
                accessorKey: "name",
                    header: "Name",
                        size: 160,
        },
        // {
        //     id: "description",
        //     accessorKey: "description",
        //     header: "Description",
        //     size: 260,
        // },
        {
            id: "duration",
                accessorKey: "duration",
                    header: "Duration",
                        cell: ({ row }) => {
                            const duration = row.getValue("duration");
                            return <div className="text-center">{duration as number}</div>
                        },
                            filterFn: (row, columnId, filterValue) => {
                                // needs to match exact number
                                const value = row.getValue(columnId) as number;
                                const filterNumber = typeof filterValue === "string" ? parseInt(filterValue, 10) : filterValue;
                                return value === filterNumber;
                            },
                                size: 80,
        },
        {
            id: "startDate",
                accessorKey: "startDate",
                    header: "Start Date",
                        meta: {
                filterVariant: 'date-range',
            },
            cell: ({ row }) => {
                const startDate = row.getValue("startDate");
                return <div className="text-center">{format((startDate as Date), dateformat)}</div>
            },
                filterFn: (row, columnId, filterValue) => {
                    const value = new Date(row.getValue(columnId));
                    const from = filterValue.from ? new Date(filterValue.from) : null;
                    const to = filterValue.to ? new Date(filterValue.to) : null;
                    if (!from || !to) return true;
                    return value >= from && value <= to;
                },
                    size: 110,
        },
        {
            id: "finishDate",
                accessorKey: "finishDate",
                    header: "Finish Date",
                        meta: {
                filterVariant: 'date-range',
            },
            cell: ({ row }) => {
                const finishDate = row.getValue("finishDate");
                return <div className={"text-center"}>{format((finishDate as Date), dateformat)}</div>
            },
                filterFn: (row, columnId, filterValue) => {
                    const value = new Date(row.getValue(columnId));
                    const from = filterValue.from ? new Date(filterValue.from) : null;
                    const to = filterValue.to ? new Date(filterValue.to) : null;
                    if (!from || !to) return true;
                    return value >= from && value <= to;
                },
                    size: 110,
        },
        {
            id: "ownerUsername",
                accessorKey: "ownerUsername",
                    header: "Owner",
                        meta: {
                filterVariant: 'select',
            },
            cell: ({ row }) => {
                const ownerUsername = row.getValue("ownerUsername");
                return <div className="text-center">{ownerUsername as string}</div>
            },
                size: 200,
        },
        {
            id: "rating",
                accessorKey: "rating",
                    header: "Risk",
                        meta: {
                filterVariant: 'select',
            },
            cell: ({ row }) => {
                const risk = row.getValue("rating");
                let style = "bg-chart-green";
                if (risk === "Medium") {
                    style = "bg-chart-amber";
                } else if (risk === "High") {
                    style = "bg-chart-red";
                }
                return <div className={"text-center text-white " + style}>{risk as string}</div>
            },
                size: 80,
        },
        {
            id: "completed",
                accessorKey: "completed",
                    header: () => {
                        return (<CompletedIcon />)
                    },
                        meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const completed = row.getValue("completed");
                return <>{completed ? <Check className="m-auto" /> : ""}</>
            },
                filterFn: (row, columnId, filterValue) => {
                    // Custom filtering logic for the "accepted" column
                    if (filterValue === "true") {
                        return row.getValue(columnId) === true; // Show only true values
                    } else if (filterValue === "false") {
                        return row.getValue(columnId) === false; // Show only false values
                    } else {
                        return true; // Show all values if filterValue is not specified (e.g., reset/all)
                    }
                },
                    size: 80,
        },
        {
            id: "completedDate",
                accessorKey: "completedDate",
                    header: "Completed Date",
                        meta: {
                filterVariant: 'date-range',
            },
            cell: ({ row }) => {
                const completedDate = row.getValue("completedDate");
                return <div className={"text-center"}>{completedDate ? format((completedDate as Date), dateformat) : "-"}</div>;
            },
                filterFn: (row, columnId, filterValue) => {
                    const value = new Date(row.getValue(columnId));
                    const from = filterValue.from ? new Date(filterValue.from) : null;
                    const to = filterValue.to ? new Date(filterValue.to) : null;
                    if (!from || !to) return true;
                    return value >= from && value <= to;
                },
                    size: 110,
        },
        {
            id: "isDeliverable",
                accessorKey: "isDeliverable",
                    header: () => {
                        return (<DeliverableIcon />)
                    },
                        meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const isDeliverable = row.getValue("isDeliverable");
                return <>{isDeliverable ? <Check className="m-auto" /> : ""}</>
            },
                filterFn: (row, columnId, filterValue) => {
                    // Custom filtering logic for the "accepted" column
                    if (filterValue === "true") {
                        return row.getValue(columnId) === true; // Show only true values
                    } else if (filterValue === "false") {
                        return row.getValue(columnId) === false; // Show only false values
                    } else {
                        return true; // Show all values if filterValue is not specified (e.g., reset/all)
                    }
                },
                    size: 80,
        },
        {
            id: "isMilestone",
                accessorKey: "isMilestone",
                    header: () => { return (<MilestoneIcon />) },
                        meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const isMilestone = row.getValue("isMilestone");
                return <>{isMilestone ? <Check className="m-auto" /> : ""}</>
            },
                filterFn: (row, columnId, filterValue) => {
                    // Custom filtering logic for the "accepted" column
                    if (filterValue === "true") {
                        return row.getValue(columnId) === true; // Show only true values
                    } else if (filterValue === "false") {
                        return row.getValue(columnId) === false; // Show only false values
                    } else {
                        return true; // Show all values if filterValue is not specified (e.g., reset/all)
                    }
                },
                    size: 80,
        },
        {
            id: "displayOnCharter",
                accessorKey: "displayOnCharter",
                    header: () => { return (<CharterIcon />) },
                        meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const displayOnCharter = row.getValue("displayOnCharter");
                return <>{displayOnCharter ? <Check className="m-auto" /> : ""}</>
            },
                filterFn: (row, columnId, filterValue) => {
                    // Custom filtering logic for the "accepted" column
                    if (filterValue === "true") {
                        return row.getValue(columnId) === true; // Show only true values
                    } else if (filterValue === "false") {
                        return row.getValue(columnId) === false; // Show only false values
                    } else {
                        return true; // Show all values if filterValue is not specified (e.g., reset/all)
                    }
                },
                    size: 80,
        },
        {
            id: "displayOnStatusReport",
                accessorKey: "displayOnStatusReport",
                    header: () => {
                        return (<StatusReportIcon />)
                    },
                        meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const displayOnStatusReport = row.getValue("displayOnStatusReport");
                return <>{displayOnStatusReport ? <Check className="m-auto" /> : ""}</>
            },
                filterFn: (row, columnId, filterValue) => {
                    // Custom filtering logic for the "accepted" column
                    if (filterValue === "true") {
                        return row.getValue(columnId) === true; // Show only true values
                    } else if (filterValue === "false") {
                        return row.getValue(columnId) === false; // Show only false values
                    } else {
                        return true; // Show all values if filterValue is not specified (e.g., reset/all)
                    }
                },
                    size: 80,
        },
        {
            id: "hasDependencies",
                accessorKey: "hasDependencies",
                    header: () => {
                        return (<DependenciesIcon />)
                    },
                        meta: {
                filterVariant: 'bool-select',
            },
            cell: ({ row }) => {
                const hasDependencies = row.getValue("hasDependencies");
                return <>{hasDependencies ? <Check className="m-auto" /> : ""}</>
            },
                filterFn: (row, columnId, filterValue) => {
                    // Custom filtering logic for the "accepted" column
                    if (filterValue === "true") {
                        return (row.getValue(columnId) as number[]).length > 0; // Show only tasks with dependencies
                    } else if (filterValue === "false") {
                        return (row.getValue(columnId) as number[]).length === 0; // Show only tasks without dependencies
                    } else {
                        return true; // Show all values if filterValue is not specified (e.g., reset/all)
                    }
                },
                    size: 80,
        });

        if (app.authLevel > 3 && !lock.viewOnly) {
            columns.push(
                {
                    id: "actions",
                    cell: ({ row }) => {
                        const task = row.original

                        return (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="h-8 w-8 p-0">
                                        <span className="sr-only">Open menu</span>
                                        <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                    <DropdownMenuItem
                                        onClick={() => HandleEditClicked(task.id)}
                                    >
                                        Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={() => HandleCompleteClicked([task.id])}
                                    >
                                        Complete
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={() => HandleDeleteClicked([task.id])}
                                    >
                                        Delete
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )
                    },
                });
        }
        return columns;
    }

    export const columnDisplayNames: [string, string][] = [
        ["id", "ID"],
        ["parentTaskId", "Parent Task"],
        ["workstream", "Workstream"],
        ["workPackage", "Work Package"],
        ["name", "Name"],
        // ["description", "Description"],
        ["startDate", "Start Date"],
        ["finishDate", "Finish Date"],
        ["completed", "Completed"],
        ["completedDate", "Completed Date"],
        ["isDeliverable", "Deliverable"],
        ["isMilestone", "Milestone"],
        ["displayOnCharter", "Display On Charter"],
        ["displayOnStatusReport", "Display On Status Report"],
        ["hasDependencies", "Dependencies"],
        ["ownerUsername", "Owner"],
        ["rating", "Risk"]
    ];